import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Plane,
  FileText,
  ScrollText,
  ShieldCheck,
  Clock,
  Globe2,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { WHATSAPP_URL, whatsappUrl } from "@/lib/pdf";

const STEPS = [
  {
    icon: FileText,
    title: "Tell us about your Indian assets",
    body: "List properties, bank accounts, fixed deposits, shares, mutual funds, jewellery and heirlooms that sit within India — from anywhere in the world.",
  },
  {
    icon: ScrollText,
    title: "Generate your India-law will online",
    body: "Our drafting engine prepares a will structured under the Indian Succession Act, 1925 — specifically scoped to your Indian estate, in minutes.",
  },
  {
    icon: ShieldCheck,
    title: "Optional Senior Lawyer review",
    body: "Our legal consultants are senior advocates specialising exclusively in succession, testamentary and estate planning law — ensuring every Will meets the highest legal standard.",
  },
  {
    icon: Plane,
    title: "Sign, witness, store — from abroad",
    body: "Download the PDF, sign in the presence of two witnesses (any adults of sound mind) wherever you live, and store a copy with a trusted family member in India.",
  },
];

const ADVANTAGES = [
  { icon: Globe2, text: "Works from USA, UK, UAE, Canada, Singapore, Australia — anywhere" },
  { icon: Clock, text: "Complete the process in 10 minutes, no travel required" },
  { icon: ShieldCheck, text: "Scoped only to Indian assets — keeps foreign will uncomplicated" },
];

export default function NRISection() {
  return (
    <section className="bg-navy py-20 text-white sm:py-24" data-testid="nri-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          {/* Left — intro */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-gold backdrop-blur">
              <Plane size={12} /> For NRIs
            </div>
            <h2 className="mt-6 font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">
              Living abroad? <span className="text-gold">Secure your Indian assets from anywhere.</span>
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-white/75 sm:text-base">
              If you're an NRI, OCI, or PIO with property, bank accounts, investments or
              ancestral assets in India, Indian law governs how they are passed on — regardless
              of where you live. A separate India-focused will ensures your family doesn't get
              stuck in multi-jurisdictional probate nightmares.
            </p>

            <ul className="mt-8 space-y-3">
              {ADVANTAGES.map((a, i) => {
                const Icon = a.icon;
                return (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/85">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gold/15 text-gold">
                      <Icon size={14} />
                    </span>
                    <span>{a.text}</span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/will-generator">
                <Button
                  className="bg-gold px-7 text-navy hover:bg-[#E5C87A]"
                  data-testid="nri-start-cta"
                >
                  Create My India Will – ₹99 <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a
                href={whatsappUrl(
                  "Hi WillSeva, I'm an NRI and would like help preparing a will for my Indian assets. Please assist me with:\n\n1. Understanding the process for NRIs\n2. A Senior Lawyer review (₹14,999)\n3. A callback from your NRI desk\n\nThank you."
                )}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-white/30 bg-white/5 text-white hover:border-gold hover:bg-white/10 hover:text-gold"
                  data-testid="nri-whatsapp-cta"
                >
                  <MessageCircle size={16} className="mr-2" /> Chat with our NRI Desk
                </Button>
              </a>
            </div>

            <div className="mt-8 rounded-md border border-white/10 bg-white/[0.04] p-5 text-xs leading-relaxed text-white/70 backdrop-blur">
              <span className="font-semibold uppercase tracking-wider text-gold">Note:</span>{" "}
              This will covers Indian assets only. For assets held outside India, consult a
              local attorney — we strongly recommend keeping the two wills separate and
              clearly scoped to avoid revocation conflicts.
            </div>
          </div>

          {/* Right — how it works */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              How It Works for NRIs
            </div>
            <h3 className="mt-3 font-serif text-2xl text-white sm:text-3xl">
              Four simple steps. Zero flights required.
            </h3>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Card
                    key={i}
                    className="border-white/10 bg-white/[0.04] text-white backdrop-blur"
                    data-testid={`nri-step-${i}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gold/15 text-gold">
                          <Icon size={18} />
                        </div>
                        <div className="font-serif text-lg text-gold">{`0${i + 1}`}</div>
                      </div>
                      <h4 className="mt-4 font-serif text-lg text-white">{s.title}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-white/70">{s.body}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
